import React from 'react';
import { Link } from 'react-router-dom';
import { Container, Row, Col } from 'reactstrap';
import Navbar from './Navbar';
import Footer from './Footer';
import GuestChatWidget from './GuestChatWidget';
import './landing.scss';
import SeoHead from '../../../Components/Common/SeoHead';
import HelpBreadcrumbSchema from '../../../Components/Common/HelpBreadcrumbSchema';
import HelpScreenshot from './HelpScreenshot';
import type { HelpImage } from './HelpScreenshot';

const HelpArticleEbayMultistore: React.FC = () => {
  const BASE = '/images/help/ebay-multistore';

  const screenshots: Record<string, HelpImage[]> = {
    step1: [
      { src: `${BASE}/ebm-step-1.png`, alt: 'eBay multistore overview', caption: 'Step 1 – Coderom supports multiple eBay marketplaces from one account' },
    ],
    step2: [
      { src: `${BASE}/ebm-step-2.png`, alt: 'Add new eBay store', caption: 'Step 2 – Connect a new eBay store via OAuth' },
    ],
    step3: [
      { src: `${BASE}/ebm-step-3.png`, alt: 'Switch between stores', caption: 'Step 3 – Switch between connected stores from the dashboard selector' },
    ],
    step4: [
      { src: `${BASE}/ebm-step-4.png`, alt: 'Business policies configuration', caption: 'Step 4 – Choose default payment, shipping, and return policies' },
    ],
  };

  return (
    <div className="layout-wrapper landing">
      <SeoHead
        title="eBay Policies & Multi-Store | CodeRom Help"
        description="Manage multiple eBay stores and marketplace policies from one CodeRom dashboard."
      />
      <HelpBreadcrumbSchema slug="ebay-multistore" name="eBay policies & multi-store" />
      <Navbar />

      <div className="help-article-page">
        <Container>
          <Row className="justify-content-center">
            <Col lg={9} xl={8}>
              {/* Breadcrumb */}
              <div className="breadcrumb-section">
                <Link to="/help" className="breadcrumb-link">Help Center</Link>
                <span className="breadcrumb-separator">›</span>
                <span className="breadcrumb-current">eBay policies & multi-store</span>
              </div>

              {/* Article Header */}
              <div className="article-header">
                <h1>eBay policies & multi-store</h1>
                <p className="article-subtitle">
                  Manage multiple eBay stores from one Coderom account. Configure business policies, shipping profiles, and return policies for each marketplace.
                </p>
                <div className="article-meta">
                  <span className="read-time">📖 6 min read</span>
                  <span className="category">eBay Setup</span>
                </div>
              </div>

              {/* Table of Contents */}
              <div className="table-of-contents">
                <h3>📋 On this page</h3>
                <ul>
                  <li><a href="#step1">1. Multi-store overview</a></li>
                  <li><a href="#step2">2. Add an eBay store</a></li>
                  <li><a href="#step3">3. Switch between stores</a></li>
                  <li><a href="#step4">4. Business policies</a></li>
                  <li><a href="#step5">5. Shipping profiles</a></li>
                  <li><a href="#step6">6. Returns policy settings</a></li>
                </ul>
              </div>

              {/* Article Content */}
              <div className="article-content">

                {/* Step 1 */}
                <div id="step1" className="content-section">
                  <h2>Step 1: Multi-store overview</h2>
                  <p>
                    Coderom supports multiple eBay marketplaces from a single account:
                  </p>

                  <div className="marketplace-table">
                    <table>
                      <thead>
                        <tr>
                          <th>Marketplace</th>
                          <th>eBay site</th>
                          <th>Currency</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td><strong>United Kingdom</strong></td>
                          <td>ebay.co.uk</td>
                          <td>GBP (£)</td>
                        </tr>
                        <tr>
                          <td><strong>United States</strong></td>
                          <td>ebay.com</td>
                          <td>USD ($)</td>
                        </tr>
                        <tr>
                          <td><strong>Germany</strong></td>
                          <td>ebay.de</td>
                          <td>EUR (€)</td>
                        </tr>
                        <tr>
                          <td><strong>Canada</strong></td>
                          <td>ebay.ca</td>
                          <td>CAD (C$)</td>
                        </tr>
                        <tr>
                          <td><strong>Australia</strong></td>
                          <td>ebay.com.au</td>
                          <td>AUD (A$)</td>
                        </tr>
                        <tr>
                          <td><strong>Spain</strong></td>
                          <td>ebay.es</td>
                          <td>EUR (€)</td>
                        </tr>
                        <tr>
                          <td><strong>France</strong></td>
                          <td>ebay.fr</td>
                          <td>EUR (€)</td>
                        </tr>
                        <tr>
                          <td><strong>Italy</strong></td>
                          <td>ebay.it</td>
                          <td>EUR (€)</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <p>
                    Each store is managed independently — separate products, prices, orders, and settings.
                  </p>

                  <HelpScreenshot images={screenshots.step1} stepLabel="Step 1: Multi-store overview" />
                </div>

                {/* Step 2 */}
                <div id="step2" className="content-section">
                  <h2>Step 2: Add an eBay store</h2>
                  <ol>
                    <li>Go to <strong>Settings → eBay Connection</strong></li>
                    <li>Click <strong>Add Another Store</strong></li>
                    <li>Select the marketplace (e.g. United States)</li>
                    <li>Sign in with your eBay account for that marketplace</li>
                    <li>Grant Coderom permission to manage listings</li>
                    <li>The new store appears in your store selector</li>
                  </ol>

                  <HelpScreenshot images={screenshots.step2} stepLabel="Step 2: Add an eBay store" />

                  <div className="tip-box">
                    <div className="tip-icon">💡</div>
                    <div className="tip-content">
                      <strong>Tip:</strong> You need a separate eBay seller account for each marketplace. If you only sell on eBay UK, you only need one store connected.
                    </div>
                  </div>
                </div>

                {/* Step 3 */}
                <div id="step3" className="content-section">
                  <h2>Step 3: Switch between stores</h2>
                  <p>
                    Use the store selector at the top of the dashboard to switch between your connected eBay stores. When you switch:
                  </p>
                  <ul>
                    <li>Products list shows items for that store only</li>
                    <li>Orders show that store's orders only</li>
                    <li>Settings apply to that store only</li>
                    <li>Reports show that store's data only</li>
                  </ul>

                  <HelpScreenshot images={screenshots.step3} stepLabel="Step 3: Switch between stores" />
                </div>

                {/* Step 4 */}
                <div id="step4" className="content-section">
                  <h2>Step 4: Business policies</h2>
                  <p>
                    eBay business policies define your payment, shipping, and return rules. Coderom uses these when creating listings.
                  </p>
                  <ol>
                    <li>Go to <strong>Settings → eBay Policies</strong></li>
                    <li>Your existing eBay business policies are synced automatically</li>
                    <li>Choose a default policy for:
                      <ul>
                        <li><strong>Payment policy</strong> — how buyers pay</li>
                        <li><strong>Shipping policy</strong> — delivery options and costs</li>
                        <li><strong>Return policy</strong> — your return terms</li>
                      </ul>
                    </li>
                  </ol>

                  <HelpScreenshot images={screenshots.step4} stepLabel="Step 4: Business policies" />

                  <div className="tip-box">
                    <div className="tip-icon">💡</div>
                    <div className="tip-content">
                      <strong>Tip:</strong> Create your business policies on eBay first (My eBay → Account → Business Policies), then sync them in Coderom. Coderom reads your policies but doesn't create them.
                    </div>
                  </div>
                </div>

                {/* Step 5 */}
                <div id="step5" className="content-section">
                  <h2>Step 5: Shipping profiles</h2>
                  <p>
                    Configure shipping for your listings:
                  </p>

                  <div className="shipping-table">
                    <table>
                      <thead>
                        <tr>
                          <th>Setting</th>
                          <th>What it does</th>
                          <th>Recommended</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td><strong>Default shipping policy</strong></td>
                          <td>Applied to all new listings</td>
                          <td>Your standard delivery policy</td>
                        </tr>
                        <tr>
                          <td><strong>Dispatch time</strong></td>
                          <td>How quickly you ship (business days)</td>
                          <td>2-3 days</td>
                        </tr>
                        <tr>
                          <td><strong>Free shipping</strong></td>
                          <td>Offer free delivery</td>
                          <td>Recommended for UK</td>
                        </tr>
                        <tr>
                          <td><strong>International shipping</strong></td>
                          <td>Ship to other countries</td>
                          <td>Optional</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Step 6 */}
                <div id="step6" className="content-section">
                  <h2>Step 6: Returns policy settings</h2>

                  <div className="returns-table">
                    <table>
                      <thead>
                        <tr>
                          <th>Setting</th>
                          <th>What it does</th>
                          <th>Recommended</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td><strong>Accept returns</strong></td>
                          <td>Allow buyer returns</td>
                          <td>Yes (30 days)</td>
                        </tr>
                        <tr>
                          <td><strong>Return shipping</strong></td>
                          <td>Who pays for return postage</td>
                          <td>Buyer pays</td>
                        </tr>
                        <tr>
                          <td><strong>Refund method</strong></td>
                          <td>How refunds are processed</td>
                          <td>Money back</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* What's Next */}
                <div className="whats-next-section">
                  <h2>What's next?</h2>
                  <p>Now that your eBay setup is complete, explore these features:</p>
                  <div className="next-steps">
                    <Link to="/help/reports" className="next-step-card">
                      <div className="next-step-icon">📊</div>
                      <div className="next-step-content">
                        <h4>Daily reports & notifications</h4>
                        <p>Get daily summaries of sales, profit, and inventory updates</p>
                      </div>
                    </Link>
                    <Link to="/help/getting-started" className="next-step-card">
                      <div className="next-step-icon">🚀</div>
                      <div className="next-step-content">
                        <h4>Getting started with Coderom</h4>
                        <p>Complete setup guide from account creation to first sale</p>
                      </div>
                    </Link>
                    <Link to="/help/pricing" className="next-step-card">
                      <div className="next-step-icon">💰</div>
                      <div className="next-step-content">
                        <h4>Smart repricing & pricing rules</h4>
                        <p>Automatically adjust prices based on Amazon changes</p>
                      </div>
                    </Link>
                  </div>
                </div>

                {/* Was This Helpful */}
                <div className="helpful-section">
                  <h3>Was this helpful?</h3>
                  <div className="helpful-buttons">
                    <button className="helpful-btn helpful-yes">👍 Yes</button>
                    <button className="helpful-btn helpful-no">👎 No</button>
                  </div>
                  <p className="helpful-text">
                    Still need help? <Link to="/help">Browse all articles</Link> or{' '}
                    <a href="mailto:support@coderom.com">contact support</a>.
                  </p>
                </div>

              </div>
            </Col>
          </Row>
        </Container>
      </div>

      <style>{`
        .help-article-page {
          padding: 120px 0 80px;
          background: linear-gradient(180deg, #f8fafc 0%, #ffffff 100%);
          min-height: 100vh;
        }

        /* Breadcrumb */
        .breadcrumb-section {
          margin-bottom: 24px;
          font-size: 14px;
        }
        .breadcrumb-link {
          color: #6366f1;
          text-decoration: none;
          transition: color 0.2s;
        }
        .breadcrumb-link:hover {
          color: #4f46e5;
        }
        .breadcrumb-separator {
          margin: 0 8px;
          color: #cbd5e1;
        }
        .breadcrumb-current {
          color: #64748b;
        }

        /* Article Header */
        .article-header {
          margin-bottom: 40px;
        }
        .article-header h1 {
          font-size: 42px;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 16px;
          line-height: 1.2;
        }
        .article-subtitle {
          font-size: 18px;
          color: #64748b;
          line-height: 1.6;
          margin-bottom: 20px;
        }
        .article-meta {
          display: flex;
          gap: 16px;
          align-items: center;
          font-size: 14px;
        }
        .read-time {
          color: #64748b;
        }
        .category {
          background: #f1f5f9;
          color: #6366f1;
          padding: 4px 12px;
          border-radius: 12px;
          font-weight: 500;
        }

        /* Table of Contents */
        .table-of-contents {
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          padding: 24px;
          margin-bottom: 40px;
        }
        .table-of-contents h3 {
          font-size: 16px;
          font-weight: 600;
          color: #1e293b;
          margin-bottom: 16px;
        }
        .table-of-contents ul {
          list-style: none;
          padding: 0;
          margin: 0;
        }
        .table-of-contents li {
          margin-bottom: 8px;
        }
        .table-of-contents a {
          color: #6366f1;
          text-decoration: none;
          font-size: 15px;
          transition: color 0.2s;
        }
        .table-of-contents a:hover {
          color: #4f46e5;
          text-decoration: underline;
        }

        /* Article Content */
        .article-content {
          color: #334155;
          font-size: 16px;
          line-height: 1.8;
        }
        .content-section {
          margin-bottom: 48px;
          scroll-margin-top: 100px;
        }
        .content-section h2 {
          font-size: 28px;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 16px;
          margin-top: 40px;
        }
        .content-section h3 {
          font-size: 20px;
          font-weight: 600;
          color: #334155;
          margin-top: 24px;
          margin-bottom: 12px;
        }
        .content-section p {
          margin-bottom: 16px;
        }
        .content-section ul,
        .content-section ol {
          margin-bottom: 16px;
          padding-left: 24px;
        }
        .content-section li {
          margin-bottom: 8px;
        }
        .content-section strong {
          color: #1e293b;
          font-weight: 600;
        }

        /* Tip Box */
        .tip-box {
          display: flex;
          gap: 12px;
          background: #eff6ff;
          border-left: 4px solid #3b82f6;
          padding: 16px;
          border-radius: 8px;
          margin: 24px 0;
        }
        .tip-icon {
          font-size: 20px;
          flex-shrink: 0;
        }
        .tip-content {
          font-size: 15px;
          line-height: 1.6;
          color: #1e40af;
        }

        /* Tables */
        .marketplace-table,
        .shipping-table,
        .returns-table {
          margin: 24px 0;
          overflow-x: auto;
        }
        .marketplace-table table,
        .shipping-table table,
        .returns-table table {
          width: 100%;
          border-collapse: collapse;
          background: white;
          border-radius: 8px;
          overflow: hidden;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
        }
        .marketplace-table th,
        .shipping-table th,
        .returns-table th {
          background: #f8fafc;
          color: #1e293b;
          font-weight: 600;
          text-align: left;
          padding: 12px 16px;
          border-bottom: 2px solid #e2e8f0;
          font-size: 14px;
        }
        .marketplace-table td,
        .shipping-table td,
        .returns-table td {
          padding: 12px 16px;
          border-bottom: 1px solid #f1f5f9;
          font-size: 15px;
        }
        .marketplace-table tbody tr:last-child td,
        .shipping-table tbody tr:last-child td,
        .returns-table tbody tr:last-child td {
          border-bottom: none;
        }
        .marketplace-table tbody tr:hover,
        .shipping-table tbody tr:hover,
        .returns-table tbody tr:hover {
          background: #f8fafc;
        }

        /* What's Next Section */
        .whats-next-section {
          margin-top: 60px;
          padding-top: 40px;
          border-top: 2px solid #e2e8f0;
        }
        .whats-next-section h2 {
          font-size: 28px;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 16px;
        }
        .next-steps {
          display: flex;
          flex-direction: column;
          gap: 16px;
          margin-top: 24px;
        }
        .next-step-card {
          display: flex;
          gap: 16px;
          padding: 20px;
          background: white;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          text-decoration: none;
          transition: all 0.3s;
        }
        .next-step-card:hover {
          border-color: #6366f1;
          box-shadow: 0 4px 12px rgba(99, 102, 241, 0.15);
          transform: translateY(-2px);
        }
        .next-step-icon {
          font-size: 32px;
          flex-shrink: 0;
        }
        .next-step-content h4 {
          font-size: 18px;
          font-weight: 600;
          color: #1e293b;
          margin-bottom: 4px;
        }
        .next-step-content p {
          font-size: 14px;
          color: #64748b;
          margin: 0;
        }

        /* Was This Helpful */
        .helpful-section {
          margin-top: 60px;
          padding: 32px;
          background: #f8fafc;
          border-radius: 12px;
          text-align: center;
        }
        .helpful-section h3 {
          font-size: 20px;
          font-weight: 600;
          color: #1e293b;
          margin-bottom: 20px;
        }
        .helpful-buttons {
          display: flex;
          gap: 12px;
          justify-content: center;
          margin-bottom: 16px;
        }
        .helpful-btn {
          padding: 10px 24px;
          border: 2px solid #e2e8f0;
          background: white;
          border-radius: 8px;
          font-size: 15px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
        }
        .helpful-btn:hover {
          border-color: #6366f1;
          background: #eff6ff;
        }
        .helpful-text {
          font-size: 14px;
          color: #64748b;
          margin: 0;
        }
        .helpful-text a {
          color: #6366f1;
          text-decoration: none;
        }
        .helpful-text a:hover {
          text-decoration: underline;
        }

        /* Responsive */
        @media (max-width: 768px) {
          .help-article-page {
            padding: 100px 0 60px;
          }
          .article-header h1 {
            font-size: 32px;
          }
          .article-subtitle {
            font-size: 16px;
          }
          .content-section h2 {
            font-size: 24px;
          }
          .marketplace-table,
          .shipping-table,
          .returns-table {
            font-size: 14px;
          }
          .next-step-card {
            flex-direction: column;
            text-align: center;
          }
        }
      `}</style>

      <Footer />
      <GuestChatWidget />
    </div>
  );
};

export default HelpArticleEbayMultistore;
